import { TransactionType, type AccountWithBuckets, type Bucket } from '@financial/shared';

export interface BalanceTarget {
  accountId: string;
  bucketId?: string;
}

export interface ResolvedBalanceTarget {
  account: AccountWithBuckets;
  bucket?: Bucket;
}

export interface BalanceAmountChange {
  before: number;
  after: number;
}

export interface AccountBalanceChange {
  account: AccountWithBuckets;
  total: BalanceAmountChange;
  available: BalanceAmountChange;
  buckets: Array<{
    bucket: Bucket;
    balance: BalanceAmountChange;
  }>;
}

/** El saldo total de la cuenta ya incluye lo que está separado en apartados. */
export function accountAvailableBalance(account: AccountWithBuckets): number {
  return account.balance - account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
}

export function resolveBalanceTarget(
  accounts: AccountWithBuckets[],
  target: BalanceTarget,
): ResolvedBalanceTarget | null {
  const account = accounts.find((candidate) => candidate.id === target.accountId);
  if (!account) return null;
  if (!target.bucketId) return { account };
  const bucket = account.buckets.find((candidate) => candidate.id === target.bucketId);
  return bucket ? { account, bucket } : null;
}

/** Dinero que realmente puede salir de la punta elegida. */
export function targetAvailableBalance(target: ResolvedBalanceTarget): number {
  return target.bucket?.balance ?? accountAvailableBalance(target.account);
}

/** Vista previa de un movimiento creado desde el saldo disponible de una cuenta. */
export function transactionBalanceChange(
  account: AccountWithBuckets,
  type: TransactionType,
  amount: number,
): AccountBalanceChange {
  const delta = type === TransactionType.INCOME ? amount : -amount;
  const available = accountAvailableBalance(account);
  return {
    account,
    total: { before: account.balance, after: account.balance + delta },
    available: { before: available, after: available + delta },
    buckets: [],
  };
}

/**
 * Replica las dos piernas que aplica TransfersService. Acumular los deltas
 * permite cubrir también disponible ↔ apartado y apartado ↔ apartado dentro
 * de una misma cuenta sin casos especiales.
 */
export function transferBalanceChanges(
  accounts: AccountWithBuckets[],
  from: BalanceTarget,
  to: BalanceTarget,
  amount: number,
): AccountBalanceChange[] {
  const fromTarget = resolveBalanceTarget(accounts, from);
  const toTarget = resolveBalanceTarget(accounts, to);
  if (!fromTarget || !toTarget) return [];

  const totalDeltas = new Map<string, number>();
  const bucketDeltas = new Map<string, number>();
  const addTotalDelta = (accountId: string, delta: number) => {
    totalDeltas.set(accountId, (totalDeltas.get(accountId) ?? 0) + delta);
  };
  const addBucketDelta = (bucketId: string | undefined, delta: number) => {
    if (!bucketId) return;
    bucketDeltas.set(bucketId, (bucketDeltas.get(bucketId) ?? 0) + delta);
  };

  addTotalDelta(from.accountId, -amount);
  addBucketDelta(from.bucketId, -amount);
  addTotalDelta(to.accountId, amount);
  addBucketDelta(to.bucketId, amount);

  const affectedIds = [...new Set([from.accountId, to.accountId])];
  return affectedIds.flatMap((accountId) => {
    const account = accounts.find((candidate) => candidate.id === accountId);
    if (!account) return [];
    const totalBefore = account.balance;
    const totalAfter = totalBefore + (totalDeltas.get(accountId) ?? 0);
    const bucketChanges = account.buckets
      .filter((bucket) => bucketDeltas.has(bucket.id))
      .map((bucket) => ({
        bucket,
        balance: {
          before: bucket.balance,
          after: bucket.balance + (bucketDeltas.get(bucket.id) ?? 0),
        },
      }));
    const bucketTotalAfter = account.buckets.reduce(
      (sum, bucket) => sum + bucket.balance + (bucketDeltas.get(bucket.id) ?? 0),
      0,
    );

    return [
      {
        account,
        total: { before: totalBefore, after: totalAfter },
        available: {
          before: accountAvailableBalance(account),
          after: totalAfter - bucketTotalAfter,
        },
        buckets: bucketChanges,
      },
    ];
  });
}
